import { useVerifyOtp, useResendOtp } from '@/hooks/useAuth';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, KeyRound } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const otpSchema = z.object({
  code: z
    .string()
    .length(6, 'auth.validation.otp.codeLength')
    .regex(/^\d+$/, 'auth.validation.otp.codeDigits'),
});

interface OtpVerificationFormProps {
  sessionId: string;
  onBack?: () => void;
  /**
   * [AUTO-INIT VAULT AU LOGIN 2026-07] Mot de passe saisi à l'étape précédente,
   * transmis en mémoire pour initialiser automatiquement un coffre en attente
   * une fois le code validé. Jamais envoyé au serveur ni persisté.
   */
  loginPassword?: string;
}

export const OtpVerificationForm = ({
  sessionId,
  onBack,
  loginPassword,
}: OtpVerificationFormProps) => {
  const { t } = useTranslation();
  const verifyOtp = useVerifyOtp(loginPassword);
  const resendOtp = useResendOtp();
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<{ code: string }>({
    resolver: zodResolver(otpSchema),
  });

  const onSubmit = async (data: { code: string }) => {
    try {
      await verifyOtp.mutateAsync({
        sessionId,
        code: data.code,
      });
    } catch {
      // Error handled by hook
    }
  };

  const handleResend = async () => {
    try {
      await resendOtp.mutateAsync(sessionId);
      setCooldown(60);
    } catch {
      // Error handled by hook
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
          <Mail className="h-8 w-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-neutral-900">
          {t('auth.otp.verificationTitle')}
        </h2>
        <p className="mt-2 text-sm text-neutral-600">
          {t('auth.otp.verificationDescription')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label
            htmlFor="code"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('auth.otp.codeLabel')}
          </label>
          <div className="relative mt-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <KeyRound className="h-5 w-5 text-neutral-400" />
            </div>
            <input
              {...register('code')}
              type="text"
              id="code"
              maxLength={6}
              placeholder="000000"
              className="block w-full rounded-xl border border-neutral-300 py-2 pl-10 pr-3 text-center text-2xl font-mono tracking-wider shadow-sm transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="\d{6}"
              autoFocus
            />
          </div>
          {errors.code && (
            <p className="mt-1 text-sm text-error-600">
              {t(errors.code.message as string)}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex w-full justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
          >
            {isSubmitting
              ? t('common.verifying')
              : t('auth.otp.verify')}
          </button>
        </div>

        {/* Resend Button */}
        <div className="text-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0 || resendOtp.isPending}
            className="text-sm font-medium text-brand-600 transition-colors hover:text-brand-500 disabled:text-neutral-400 disabled:cursor-not-allowed"
          >
            {cooldown > 0
              ? t('auth.otp.resendIn', { seconds: cooldown })
              : t('auth.otp.resend')}
          </button>
        </div>

        {/* Back Button */}
        {onBack && (
          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-sm font-medium text-brand-600 transition-colors hover:text-brand-500"
            >
              {t('auth.otp.backToLogin')}
            </button>
          </div>
        )}
      </form>

      {/* Help Text */}
      <div className="rounded-lg border border-info-200 bg-info-50 p-3">
        <p className="text-xs text-info-700">
          {t('auth.otp.helpText')}
        </p>
      </div>
    </div>
  );
};
